import { useState, useEffect, useRef } from 'react';
import { useToast } from '../contexts/ToastContext';
import api, { API_URL } from '../utils/api';
import Icons from '../utils/icons';
import { fontStack, dInput, btnPrimary, btnSecondary, btnSmall } from '../styles/shared';
import ConfirmModal from './ConfirmModal';
import useConfirm from '../hooks/useConfirm';

/* ── SVG Icons ─────────────────────────────────────── */
const FolderIcon = ({ s = 16, c = 'currentColor' }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>;
const ImgIcon  = ({ s = 16, c = 'currentColor' }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;
const VidIcon  = ({ s = 16, c = 'currentColor' }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>;
const PlayIcon = ({ s = 30 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="rgba(255,255,255,0.9)" stroke="none"><polygon points="5 3 19 12 5 21 5 3"/></svg>;
const PenIcon  = ({ s = 11 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const TrashIcon= ({ s = 11 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>;
const MoveIcon = ({ s = 11 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>;

/* ── Video Thumbnail — captures first frame to canvas for speed ── */
function VideoThumb({ src, style }) {
    const canvasRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.muted = true;
        video.preload = 'metadata';
        video.src = src;

        video.onloadeddata = () => {
            if (cancelled) return;
            video.currentTime = 0.1;
        };
        video.onseeked = () => {
            if (cancelled) return;
            try {
                const canvas = canvasRef.current;
                if (canvas) {
                    // Low quality small thumbnail
                    canvas.width = 160;
                    canvas.height = 160;
                    const ctx = canvas.getContext('2d');
                    const vw = video.videoWidth || 160;
                    const vh = video.videoHeight || 160;
                    const scale = Math.max(160 / vw, 160 / vh);
                    const sw = vw * scale, sh = vh * scale;
                    ctx.drawImage(video, (160 - sw) / 2, (160 - sh) / 2, sw, sh);
                    setReady(true);
                }
            } catch {}
            // Release video resources
            video.src = '';
            video.load();
        };
        video.onerror = () => { if (!cancelled) setFailed(true); };

        return () => { cancelled = true; video.src = ''; video.load(); };
    }, [src]);

    return (
        <div style={{ ...style, position: 'relative', background: 'linear-gradient(135deg, #1a1a2e, #16213e)' }}>
            <canvas ref={canvasRef} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: ready ? 1 : 0, display: 'block' }} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: ready ? 'rgba(0,0,0,0.2)' : 'transparent', pointerEvents: 'none' }}>
                <PlayIcon s={ready ? 22 : 28} />
            </div>
        </div>
    );
}

/* ── Main Component ────────────────────────────────── */
export default function VaultTab({ channel, isChatter = false }) {
    const toast = useToast();
    const { confirm, confirmProps } = useConfirm();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState('');
    const [search, setSearch] = useState('');
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [lightbox, setLightbox] = useState(null);
    const [renaming, setRenaming] = useState(null);
    const [renameText, setRenameText] = useState('');
    const [activeFolder, setActiveFolder] = useState('all');
    const [folders, setFolders] = useState([]);
    const [showNewFolder, setShowNewFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [renamingFolder, setRenamingFolder] = useState(null);
    const [renameFolderText, setRenameFolderText] = useState('');
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [selectMode, setSelectMode] = useState(false);
    const fileInputRef = useRef(null);

    const fileUrl = (fid, thumb) => `${API_URL}/api/dashboard/file?file_id=${encodeURIComponent(fid)}${thumb ? '&thumb=1' : ''}&auth=${encodeURIComponent(localStorage.getItem('token') || '')}`;

    /* ── Data Loading ── */
    const loadItems = async (p, q, f) => {
        try {
            let url = `/api/vault/list?channel=${encodeURIComponent(channel)}&page=${p}&per_page=60`;
            if (q) url += `&search=${encodeURIComponent(q)}`;
            if (f && f !== 'all') url += `&folder=${encodeURIComponent(f)}`;
            const res = await api.get(url);
            setItems(res.data.items || []);
            setTotal(res.data.total || 0);
        } catch (err) { console.error('Vault load error:', err); }
        finally { setLoading(false); }
    };

    const loadFolders = async () => {
        try {
            const res = await api.get(`/api/vault/folders?channel=${encodeURIComponent(channel)}`);
            setFolders(res.data.folders || []);
        } catch (err) {}
    };

    useEffect(() => {
        setLoading(true); setPage(1); setActiveFolder('all'); setSearch('');
        loadItems(1, '', 'all');
        loadFolders();
    }, [channel]);

    const switchFolder = (f) => { setActiveFolder(f); setPage(1); setLoading(true); setSelectedIds(new Set()); setSelectMode(false); loadItems(1, search, f); };
    const doSearch    = ()  => { setPage(1); setLoading(true); loadItems(1, search, activeFolder); };
    const changePage  = (p) => { setPage(p); loadItems(p, search, activeFolder); };

    /* ── Upload ── */
    const handleUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        let ok = 0;
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            setUploadProgress(`${i + 1}/${files.length}`);
            if (file.size > 100 * 1024 * 1024) { toast.error(`${file.name} too large (max 100MB)`); continue; }
            const fd = new FormData();
            fd.append('token', localStorage.getItem('token'));
            fd.append('channel', channel);
            fd.append('file', file);
            fd.append('title', file.name.replace(/\.[^.]+$/, ''));
            const isVid = file.type.startsWith('video/') || /\.(mp4|mov|webm|avi)$/i.test(file.name);
            // custom folders get files directly; built-in folders auto-sort by type
            let folder = activeFolder;
            if (!folder || folder === 'all' || folder === 'images' || folder === 'videos') {
                folder = isVid ? 'videos' : 'images';
            }
            fd.append('folder', folder);
            try { await api.post('/api/vault/upload', fd); ok++; }
            catch { toast.error(`Failed: ${file.name}`); }
        }
        setUploading(false); setUploadProgress('');
        e.target.value = '';
        if (ok > 0) {
            toast.success(`${ok} file${ok > 1 ? 's' : ''} uploaded`);
            setPage(1);
            await loadItems(1, search, activeFolder);
            loadFolders();
        }
    };

    /* ── CRUD ── */
    const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

    const handleDelete = async (item) => {
        if (!await confirm({ title: 'Delete item?', message: `Delete "${item.title || item.original_filename}"?`, confirmLabel: 'Delete', danger: true })) return;
        try {
            await api.post('/api/vault/delete', { id: item.id });
            setItems(p => p.filter(i => i.id !== item.id));
            setTotal(p => p - 1);
            if (lightbox?.id === item.id) setLightbox(null);
            toast.success('Deleted');
        } catch { toast.error('Delete failed'); }
    };

    const handleBulkDelete = async () => {
        const ids = Array.from(selectedIds);
        try {
            const res = await api.post('/api/vault/delete', { ids });
            const deleted = res.data?.deleted || ids.length;
            toast.success(`${deleted} item${deleted > 1 ? 's' : ''} deleted`);
            setSelectedIds(new Set());
            setConfirmBulkDelete(false);
            await loadItems(page, search, activeFolder);
        } catch { toast.error('Delete failed'); }
    };

    const handleRename = async () => {
        if (!renaming) return;
        try {
            await api.post('/api/vault/rename', { id: renaming.id, title: renameText.trim() });
            setItems(p => p.map(i => i.id === renaming.id ? { ...i, title: renameText.trim() } : i));
            toast.success('Renamed');
        } catch { toast.error('Rename failed'); }
        setRenaming(null); setRenameText('');
    };

    const handleMove = async (item, targetFolder) => {
        try {
            await api.post('/api/vault/move', { id: item.id, folder: targetFolder });
            toast.success(`Moved to ${targetFolder}`);
        } catch { toast.error('Move failed'); }
    };

    const handleBulkMove = async (targetFolder) => {
        const ids = Array.from(selectedIds);
        let ok = 0;
        for (const id of ids) {
            try { await api.post('/api/vault/move', { id, folder: targetFolder }); ok++; } catch {}
        }
        if (ok > 0) {
            toast.success(`${ok} item${ok > 1 ? 's' : ''} moved to ${targetFolder}`);
            setSelectedIds(new Set());
            setShowMoveModal(false);
            await loadItems(page, search, activeFolder);
        }
    };

    const handleRemoveFromFolder = async (item) => {
        // Move back to type-based folder instead of deleting
        const typeFolder = item.file_type === 'video' ? 'videos' : 'images';
        try {
            await api.post('/api/vault/move', { id: item.id, folder: typeFolder });
            setItems(p => p.filter(i => i.id !== item.id));
            setTotal(p => p - 1);
            toast.success('Removed from folder');
        } catch { toast.error('Failed'); }
    };

    const handleBulkRemoveFromFolder = async () => {
        const ids = Array.from(selectedIds);
        let ok = 0;
        for (const id of ids) {
            const item = items.find(i => i.id === id);
            const typeFolder = item?.file_type === 'video' ? 'videos' : 'images';
            try { await api.post('/api/vault/move', { id, folder: typeFolder }); ok++; } catch {}
        }
        if (ok > 0) {
            toast.success(`${ok} item${ok > 1 ? 's' : ''} removed from folder`);
            setSelectedIds(new Set());
            await loadItems(page, search, activeFolder);
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const selectAll = () => { setSelectedIds(new Set(items.map(i => i.id))); };
    const clearSelection = () => { setSelectedIds(new Set()); setSelectMode(false); setConfirmBulkDelete(false); };
    const isCustomFolder = activeFolder !== 'all' && activeFolder !== 'images' && activeFolder !== 'videos';

    /* ── Folders ── */
    const handleCreateFolder = () => {
        const name = newFolderName.trim();
        if (!name) return;
        if (['all', 'images', 'videos'].includes(name.toLowerCase())) { toast.error('Reserved name'); return; }
        if (folders.includes(name)) { toast.error('Already exists'); return; }
        setFolders(p => [...p, name]);
        setShowNewFolder(false); setNewFolderName('');
        toast.success('Folder created');
    };

    const handleDeleteFolder = async (name) => {
        if (!await confirm({ title: 'Delete folder?', message: `Delete "${name}"? Media will move back to Images/Videos.`, confirmLabel: 'Delete', danger: true })) return;
        try {
            await api.post('/api/vault/folders/delete', { channel, name });
            setFolders(p => p.filter(f => f !== name));
            if (activeFolder === name) { setActiveFolder('all'); loadItems(1, search, 'all'); }
            toast.success('Folder deleted');
        } catch { toast.error('Failed'); }
    };

    const handleRenameFolder = async () => {
        if (!renamingFolder || !renameFolderText.trim()) return;
        try {
            await api.post('/api/vault/folders/rename', { channel, old_name: renamingFolder, new_name: renameFolderText.trim() });
            setFolders(p => p.map(f => f === renamingFolder ? renameFolderText.trim() : f));
            if (activeFolder === renamingFolder) setActiveFolder(renameFolderText.trim());
            toast.success('Renamed');
        } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
        setRenamingFolder(null); setRenameFolderText('');
    };

    const fmtSize = (b) => !b ? '' : b < 1048576 ? `${(b/1024).toFixed(0)}KB` : `${(b/1048576).toFixed(1)}MB`;

    const builtInFolders = [
        { id: 'all',    label: 'All Media', Ico: FolderIcon },
        { id: 'images', label: 'Images',    Ico: ImgIcon },
        { id: 'videos', label: 'Videos',    Ico: VidIcon },
    ];

    // All folders for move target (built-in type folders + custom)
    const moveTargets = [
        { id: 'images', label: 'Images' },
        { id: 'videos', label: 'Videos' },
        ...folders.map(f => ({ id: f, label: f }))
    ];

    if (loading && items.length === 0) return <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>Loading vault...</div>;

    /* ════════════════ RENDER ════════════════ */
    return (
        <div style={{ display: 'flex', gap: '16px', minHeight: '400px' }}>

            {/* ── Folder Sidebar ── */}
            <div style={{ width: '180px', flexShrink: 0 }}>
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '12px', padding: '10px', boxShadow: 'var(--card-shadow)' }}>
                    <p style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 8px', margin: '0 0 4px' }}>Folders</p>

                    {builtInFolders.map(f => (
                        <div key={f.id} onClick={() => switchFolder(f.id)}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: activeFolder === f.id ? 600 : 400, color: activeFolder === f.id ? 'var(--text)' : 'var(--text-muted)', background: activeFolder === f.id ? 'var(--sidebar-item-active)' : 'transparent', transition: 'all 0.15s', marginBottom: '2px' }}>
                            <f.Ico s={15} c={activeFolder === f.id ? 'var(--accent)' : 'var(--text-muted)'} /> {f.label}
                        </div>
                    ))}

                    {folders.length > 0 && <div style={{ borderTop: '1px solid var(--border)', margin: '6px 0', opacity: 0.4 }} />}

                    {folders.map(f => (
                        <div key={f} onClick={() => switchFolder(f)}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: activeFolder === f ? 600 : 400, color: activeFolder === f ? 'var(--text)' : 'var(--text-muted)', background: activeFolder === f ? 'var(--sidebar-item-active)' : 'transparent', transition: 'all 0.15s', marginBottom: '2px' }}>
                            <FolderIcon s={15} c={activeFolder === f ? 'var(--accent)' : 'var(--text-muted)'} />
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f}</span>
                            {activeFolder === f && (
                                <div style={{ display: 'flex', gap: '4px' }}>
                                    <span onClick={e => { e.stopPropagation(); setRenamingFolder(f); setRenameFolderText(f); }} style={{ cursor: 'pointer', opacity: 0.6, display: 'flex' }}><PenIcon s={11} /></span>
                                    {!isChatter && <span onClick={e => { e.stopPropagation(); handleDeleteFolder(f); }} style={{ cursor: 'pointer', opacity: 0.6, display: 'flex', color: '#ef4444' }}><TrashIcon s={11} /></span>}
                                </div>
                            )}
                        </div>
                    ))}

                    <div onClick={() => setShowNewFolder(true)}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', color: 'var(--accent)', marginTop: '4px' }}>
                        <Icons.Plus /> New Folder
                    </div>
                </div>
            </div>

            {/* ── Main Panel ── */}
            <div style={{ flex: 1, minWidth: 0 }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                        <h2 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text)', margin: '0 0 2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Icons.Lock /> Vault
                        </h2>
                        <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>{total} item{total !== 1 ? 's' : ''}{activeFolder !== 'all' ? ` in ${activeFolder}` : ''}</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <div style={{ position: 'relative' }}>
                            <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && doSearch()} placeholder="Search..." style={{ ...dInput, width: '160px', fontSize: '12px', padding: '8px 10px 8px 30px' }} />
                            <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', opacity: 0.5, display: 'flex' }}><Icons.Search /></span>
                        </div>
                        <input type="file" ref={fileInputRef} onChange={handleUpload} accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm" multiple style={{ display: 'none' }} />
                        <button onClick={() => { if (selectMode) { setSelectMode(false); setSelectedIds(new Set()); } else { setSelectMode(true); } }}
                            style={{ ...btnSecondary, width: 'auto', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', borderColor: selectMode ? 'var(--accent)' : undefined, color: selectMode ? 'var(--accent)' : undefined, background: selectMode ? ('rgba(0,174,239,0.06)') : undefined }}>
                            <Icons.Check /> {selectMode ? 'Cancel' : 'Select'}
                        </button>
                        <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                            style={{ ...btnPrimary, width: 'auto', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', opacity: uploading ? 0.7 : 1 }}>
                            {uploading ? <Icons.Clock /> : <Icons.Upload />} {uploading ? `Uploading ${uploadProgress}` : 'Upload'}
                        </button>
                    </div>
                </div>

                {/* Bulk Action Bar */}
                {selectMode && selectedIds.size > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', padding: '10px 14px', background: 'rgba(0,174,239,0.06)', border: '1px solid var(--accent)', borderRadius: '10px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--accent)' }}>{selectedIds.size} selected</span>
                        <button onClick={() => setShowMoveModal(true)} style={{ ...btnSmall, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--accent)' }}><MoveIcon s={12} /> Move to folder</button>
                        {isCustomFolder && <button onClick={handleBulkRemoveFromFolder} style={{ ...btnSmall, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--text-muted)' }}><Icons.X /> Remove from folder</button>}
                        {!isChatter && (confirmBulkDelete ? (
                            <>
                                <span style={{ fontSize: '11px', color: '#ef4444', fontWeight: 600 }}>Delete {selectedIds.size}?</span>
                                <button onClick={handleBulkDelete} style={{ ...btnSmall, fontSize: '11px', color: '#fff', background: '#ef4444', border: 'none', borderRadius: '6px', padding: '4px 10px', fontWeight: 600 }}>Confirm</button>
                                <button onClick={() => setConfirmBulkDelete(false)} style={{ ...btnSmall, fontSize: '11px' }}>Cancel</button>
                            </>
                        ) : (
                            <button onClick={() => setConfirmBulkDelete(true)} style={{ ...btnSmall, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#ef4444' }}><TrashIcon s={12} /> Delete</button>
                        ))}
                        <div style={{ flex: 1 }} />
                        <button onClick={selectAll} style={{ ...btnSmall, fontSize: '11px' }}>Select all</button>
                        <button onClick={clearSelection} style={{ ...btnSmall, fontSize: '11px' }}>Clear</button>
                    </div>
                )}

                {/* Grid */}
                {items.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '14px' }}>
                        <ImgIcon s={28} c="var(--text-muted)" />
                        <p style={{ color: 'var(--text-muted)', marginTop: '12px', fontSize: '13px' }}>{search ? 'No results' : 'No media in this folder'}</p>
                        {!search && <button onClick={() => fileInputRef.current?.click()} style={{ ...btnSecondary, marginTop: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}><Icons.Upload /> Upload media</button>}
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                        {items.map(item => {
                            const isSel = selectedIds.has(item.id);
                            return (
                            <div key={item.id} style={{ background: 'var(--card-bg)', border: `1px solid ${isSel ? 'var(--accent)' : 'var(--card-border)'}`, borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', transition: 'border-color 0.15s', boxShadow: isSel ? '0 0 0 1px var(--accent)' : 'var(--card-shadow)' }}
                                onMouseEnter={e => { if (!isSel) e.currentTarget.style.borderColor = 'var(--accent)'; }}
                                onMouseLeave={e => { if (!isSel) e.currentTarget.style.borderColor = 'var(--card-border)'; }}>
                                {/* Thumbnail */}
                                <div style={{ width: '100%', aspectRatio: '1', overflow: 'hidden', position: 'relative', background: 'rgba(0,0,0,0.03)' }}>
                                    <div onClick={() => { if (selectMode) toggleSelect(item.id); else setLightbox(item); }} style={{ width: '100%', height: '100%' }}>
                                        {item.file_type === 'video' ? (
                                            <VideoThumb src={fileUrl(item.file_id)} style={{ width: '100%', height: '100%' }} />
                                        ) : (
                                            <img src={fileUrl(item.file_id, true)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />
                                        )}
                                    </div>
                                    {/* Checkbox — only in select mode */}
                                    {selectMode && (
                                        <div onClick={e => { e.stopPropagation(); toggleSelect(item.id); }}
                                            style={{ position: 'absolute', top: '6px', left: '6px', width: '22px', height: '22px', borderRadius: '6px', border: `2px solid ${isSel ? 'var(--accent)' : 'rgba(255,255,255,0.5)'}`, background: isSel ? 'var(--accent)' : 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s' }}>
                                            {isSel && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                                        </div>
                                    )}
                                    <span style={{ position: 'absolute', bottom: '4px', right: '4px', background: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: '9px', padding: '1px 5px', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        {item.file_type === 'video' ? <VidIcon s={9} c="#fff" /> : <ImgIcon s={9} c="#fff" />} {fmtSize(item.file_size)}
                                    </span>
                                    {item.folder && item.folder !== 'images' && item.folder !== 'videos' && activeFolder === 'all' && (
                                        <span style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '8px', padding: '1px 5px', borderRadius: '3px' }}>{item.folder}</span>
                                    )}
                                </div>
                                {/* Info */}
                                <div style={{ padding: '6px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '28px' }}>
                                    <p style={{ fontSize: '10px', fontWeight: 500, color: 'var(--text)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: '4px' }}>
                                        {item.title || item.original_filename || 'Untitled'}
                                    </p>
                                    <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                                        <button onClick={e => { e.stopPropagation(); setRenaming(item); setRenameText(item.title || ''); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px', display: 'flex' }} title="Rename"><PenIcon s={11} /></button>
                                        {isCustomFolder ? (
                                            <button onClick={e => { e.stopPropagation(); handleRemoveFromFolder(item); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px', display: 'flex' }} title="Remove from folder"><Icons.X /></button>
                                        ) : !isChatter && (
                                            <button onClick={e => { e.stopPropagation(); handleDelete(item); }} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px', display: 'flex' }} title="Delete"><TrashIcon s={11} /></button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}

                {total > 60 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
                        <button onClick={() => changePage(Math.max(1, page - 1))} disabled={page <= 1} style={{ ...btnSmall, opacity: page <= 1 ? 0.4 : 1 }}>Prev</button>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', padding: '6px 10px' }}>Page {page}/{Math.ceil(total / 60)}</span>
                        <button onClick={() => changePage(page + 1)} disabled={page >= Math.ceil(total / 60)} style={{ ...btnSmall, opacity: page >= Math.ceil(total / 60) ? 0.4 : 1 }}>Next</button>
                    </div>
                )}
            </div>

            {/* ── Lightbox ── */}
            {lightbox && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' }} onClick={() => setLightbox(null)}>
                    <div style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }} onClick={e => e.stopPropagation()}>
                        {lightbox.file_type === 'video'
                            ? <video src={fileUrl(lightbox.file_id)} controls autoPlay style={{ maxWidth: '90vw', maxHeight: '82vh', borderRadius: '12px' }} />
                            : <img src={fileUrl(lightbox.file_id)} alt="" style={{ maxWidth: '90vw', maxHeight: '82vh', borderRadius: '12px', objectFit: 'contain' }} />}
                        <div style={{ position: 'absolute', top: '-40px', right: 0, display: 'flex', gap: '6px' }}>
                            <button onClick={() => { navigator.clipboard.writeText(lightbox.file_id); toast.success('File ID copied'); }}
                                style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}><Icons.Copy /> Copy ID</button>
                            <button onClick={() => setLightbox(null)}
                                style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: '30px', height: '30px', borderRadius: '6px', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icons.X /></button>
                        </div>
                        <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontSize: '11px', marginTop: '8px' }}>{lightbox.title || lightbox.original_filename}</p>
                    </div>
                </div>
            )}

            {/* ── Bulk Move Modal ── */}
            {showMoveModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', zIndex: 2001, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }} onClick={() => setShowMoveModal(false)}>
                    <div style={{ background: 'var(--modal-bg)', border: '1px solid var(--glass-border)', borderRadius: '14px', padding: '20px', width: '300px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text)', margin: '0 0 6px' }}>Move {selectedIds.size} item{selectedIds.size > 1 ? 's' : ''} to</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '12px' }}>
                            {moveTargets.filter(t => t.id !== activeFolder).map(t => (
                                <div key={t.id} onClick={() => handleBulkMove(t.id)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', color: 'var(--text)', background: 'rgba(0,0,0,0.03)', transition: 'background 0.15s' }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'var(--sidebar-item-active)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.03)'}>
                                    <FolderIcon s={14} c="var(--accent)" /> {t.label}
                                </div>
                            ))}
                        </div>
                        <button onClick={() => setShowMoveModal(false)} style={{ ...btnSecondary, marginTop: '12px', width: '100%' }}>Cancel</button>
                    </div>
                </div>
            )}

            {/* ── Rename Modal ── */}
            {renaming && (
                <div style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', zIndex: 2001, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }} onClick={() => { setRenaming(null); setRenameText(''); }}>
                    <div style={{ background: 'var(--modal-bg)', border: '1px solid var(--glass-border)', borderRadius: '14px', padding: '24px', width: '340px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text)', margin: '0 0 14px' }}>Rename</h3>
                        <input value={renameText} onChange={e => setRenameText(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleRename()} autoFocus style={{ ...dInput, marginBottom: '14px' }} placeholder="Title..." />
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button onClick={() => { setRenaming(null); setRenameText(''); }} style={btnSecondary}>Cancel</button>
                            <button onClick={handleRename} style={{ ...btnPrimary, width: 'auto', padding: '10px 20px' }}>Save</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── New Folder Modal ── */}
            {showNewFolder && (
                <div style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', zIndex: 2001, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }} onClick={() => { setShowNewFolder(false); setNewFolderName(''); }}>
                    <div style={{ background: 'var(--modal-bg)', border: '1px solid var(--glass-border)', borderRadius: '14px', padding: '24px', width: '340px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text)', margin: '0 0 14px' }}>New Folder</h3>
                        <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreateFolder()} autoFocus style={{ ...dInput, marginBottom: '14px' }} placeholder="Folder name..." />
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button onClick={() => { setShowNewFolder(false); setNewFolderName(''); }} style={btnSecondary}>Cancel</button>
                            <button onClick={handleCreateFolder} style={{ ...btnPrimary, width: 'auto', padding: '10px 20px' }}>Create</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Rename Folder Modal ── */}
            {renamingFolder && (
                <div style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', zIndex: 2001, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }} onClick={() => { setRenamingFolder(null); setRenameFolderText(''); }}>
                    <div style={{ background: 'var(--modal-bg)', border: '1px solid var(--glass-border)', borderRadius: '14px', padding: '24px', width: '340px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text)', margin: '0 0 14px' }}>Rename Folder</h3>
                        <input value={renameFolderText} onChange={e => setRenameFolderText(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleRenameFolder()} autoFocus style={{ ...dInput, marginBottom: '14px' }} placeholder="Folder name..." />
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button onClick={() => { setRenamingFolder(null); setRenameFolderText(''); }} style={btnSecondary}>Cancel</button>
                            <button onClick={handleRenameFolder} style={{ ...btnPrimary, width: 'auto', padding: '10px 20px' }}>Save</button>
                        </div>
                    </div>
                </div>
            )}
            <ConfirmModal {...confirmProps} />
        </div>
    );
}